import { effectiveStaticOverlays } from "./static-overlay-collection.js";
import { overlayPreview } from "./hotspot-overlay.js";

// This player owns a copy of an editor draft. It has no save or publish capability.
export class InteractionPreviewPlayer {
  constructor(items, selectedIndex = 0) {
    this.items = structuredClone(items);
    this.index = Math.max(0, Math.min(this.items.length - 1, selectedIndex));
    this.history = []; this.origin = null; this.elapsed = 0; this.pendingElapsed = 0; this.paused = false; this.scene = null;
    this.idle = 0; this.resumeDelayMs = 15000;
  }
  setScene(scene) { this.scene = scene; this.elapsed = this.pendingElapsed; this.pendingElapsed = 0; this.paused = false; }
  select(index, remember = true, elapsed = 0) {
    if (!Number.isInteger(index) || index < 0 || index >= this.items.length) return false;
    if (remember) {
      this.origin ??= { index: this.index, elapsed: this.elapsed };
      if (this.history.length >= 16) this.history.splice(1, 1);
      this.history.push({ index: this.index, elapsed: this.elapsed });
    }
    this.index = index; this.elapsed = 0; this.pendingElapsed = elapsed; this.paused = false; this.scene = null; this.idle = 0; return true;
  }
  next(delta = 1) {
    const base = this.items[this.index].autoplay !== false ? this.index : this.origin?.index ?? this.index;
    for (let step = 1; step <= this.items.length; step++) {
      const index = (base + delta * step + this.items.length * 2) % this.items.length;
      if (this.items[index].autoplay !== false) { this.history = []; this.origin = null; return this.select(index, false); }
    }
    this.paused = true; return false;
  }
  back() {
    const target = this.history.pop(); if (!target) return false;
    if (!this.history.length) this.origin = null;
    return this.select(target.index, false, target.elapsed);
  }
  togglePause() { this.paused = !this.paused; this.idle = 0; }
  get circular() { return !this.origin && this.items[this.index].loopArtwork && this.items.filter(item => item.autoplay !== false).length === 1; }
  get offset() {
    if (!this.scene) return { x: 0, y: 0 };
    const { plan, direction, speed } = this.scene;
    const horizontal = plan.outputWidth > plan.viewport.width;
    const extent = Math.max(0, horizontal ? plan.outputWidth - plan.viewport.width : plan.outputHeight - plan.viewport.height);
    const total = horizontal ? plan.outputWidth : plan.outputHeight;
    const travelled = this.circular ? this.elapsed * speed / 1000 % total : Math.min(extent, this.elapsed * speed / 1000);
    const position = [2, 4].includes(direction) ? (extent - travelled + total) % total : travelled;
    return horizontal ? { x: position, y: 0 } : { x: 0, y: position };
  }
  tick(milliseconds) {
    if (!this.scene) return false;
    const delta = Math.max(0, Math.min(250, Number(milliseconds) || 0));
    if (this.origin || this.paused) this.idle += delta;
    if (this.resumeDelayMs > 0 && this.idle >= this.resumeDelayMs) {
      this.idle = 0;
      if (this.origin) {
        const target = this.origin; this.origin = null; this.history = [];
        return this.select(target.index, false, target.elapsed);
      }
      this.paused = false;
    }
    if (this.paused) return false;
    this.elapsed += delta;
    const { plan, speed } = this.scene;
    const extent = Math.max(plan.outputWidth - plan.viewport.width, plan.outputHeight - plan.viewport.height, 0);
    const duration = extent ? extent / speed * 1000 : Math.max(100, Number(this.items[this.index].holdDurationMs ?? 5000));
    if (this.elapsed < duration) return false;
    if (this.origin || (extent && this.circular)) return false;
    return this.next();
  }
  tap(x, y) {
    if (!this.scene) return "loading";
    this.idle = 0;
    const offset = this.offset;
    const hit = (this.scene.interactions?.touchAreas ?? []).find(area => {
      const fixed = Number(area.coordinateMode) === 2, pointX = x + (fixed ? 0 : offset.x), pointY = y + (fixed ? 0 : offset.y), r = area.region;
      return r && pointX >= r.x && pointX < r.x + r.width && pointY >= r.y && pointY < r.y + r.height;
    });
    if (!hit) { if (this.scene.plan.presentation !== "fit") this.togglePause(); return this.paused ? "paused" : "playing"; }
    const action = hit.action ?? {}, kind = Number(action.kind);
    if (kind === 7) return "keep-artwork";
    if (kind === 2) return this.next() ? "navigate" : "unavailable";
    if (kind === 3) return this.next(-1) ? "navigate" : "unavailable";
    if (kind === 4) return this.back() ? "navigate" : "unavailable";
    if ([1, 5, 6].includes(kind)) {
      // The same asset can occur more than once with different overlays/settings.
      // An explicit occurrence ID must not silently fall back to a different item.
      const matches = this.items.map((item, index) => String(item.assetId) === String(action.targetContentId) ? index : -1).filter(index => index >= 0);
      const target = action.targetPlaylistItemId
        ? this.items.findIndex(item => String(item.instanceId) === String(action.targetPlaylistItemId))
        : kind === 6 ? action.targetPlaylistIndex
          : matches.length === 1 ? matches[0] : -1;
      if (kind === 6 && Number.isInteger(target) && target >= 0 && target < this.items.length && this.items[target].autoplay !== false) {
        this.history = []; this.origin = null;
        return this.select(target, false) ? "navigate" : "unavailable";
      }
      return this.select(target) ? "navigate" : "unavailable";
    }
    return "unassigned";
  }
}

export function previewPlayback(plan, item) {
  if (plan.presentation === "fit") return { direction: 1, speed: 1 };
  const vertical = plan.outputHeight > plan.viewport.height;
  const direction = Number(vertical ? item.requestedVerticalDirection ?? item.direction ?? 3 : item.requestedHorizontalDirection ?? item.direction ?? 1);
  const speed = Number(vertical ? item.requestedVerticalSpeedPxPerSecond ?? item.speedPxPerSecond ?? 5 : item.requestedHorizontalSpeedPxPerSecond ?? item.speedPxPerSecond ?? 10);
  if (!Number.isFinite(speed) || speed < (vertical ? 5 : 1) || speed > 60 || !(vertical ? [3, 4] : [1, 2]).includes(direction))
    throw new Error("Choose a valid scroll direction and a speed up to 60 pixels/second before previewing.");
  return { direction, speed };
}

function loadImage(url) {
  const image = new Image(); image.src = url;
  return image.decode().then(() => image);
}

export function openInteractionPreview({ items, selectedIndex, prepareItem, isCurrent = () => true }) {
  const player = new InteractionPreviewPlayer(items, selectedIndex), dialog = document.createElement("dialog");
  dialog.className = "interaction-preview-dialog";
  dialog.innerHTML = '<h2>Test interactions preview</h2><p>Tap a hotspot to test its action. Tap elsewhere to pause or resume. This uses your current draft and does not send anything to Pirdy.</p><div class="interaction-preview-screen"><canvas tabindex="0" aria-label="Interactive Pirdy screen; click to test hotspots, space to pause"></canvas></div><p data-preview-status role="status" aria-live="polite">Preparing artwork...</p><div class="actions"><button type="button" class="button" data-preview-back>Back</button><button type="button" class="button" data-preview-previous>Previous</button><button type="button" class="button" data-preview-pause>Pause</button><button type="button" class="button" data-preview-next>Next</button><button type="button" class="button" data-preview-retry hidden>Retry</button><button type="button" class="button primary" data-preview-close>Close preview</button></div><label>Automatic resume in this preview<select data-preview-resume><option value="15000">15 seconds</option><option value="30000">30 seconds</option><option value="60000">60 seconds</option><option value="0">Off</option></select></label><p class="field-help">Match this to the resume setting on your Pirdy. Scrolling, overlays and hotspot navigation are simulated. Check audio and transition effects on your Pirdy.</p>';
  document.body.append(dialog);
  const canvas = dialog.querySelector("canvas"), context = canvas.getContext("2d"), status = dialog.querySelector("[data-preview-status]"), pause = dialog.querySelector("[data-preview-pause]"), retry = dialog.querySelector("[data-preview-retry]");
  const navigation = dialog.querySelectorAll("[data-preview-next], [data-preview-previous], [data-preview-back]");
  let closed = false, generation = 0, frame = 0, last = null, loaded = null;
  const cache = new Map();
  const close = () => { if (closed) return; closed = true; generation++; cancelAnimationFrame(frame); dialog.close(); dialog.remove(); cache.clear(); };
  const update = message => { status.textContent = message ?? `${loaded?.name ?? "Artwork"} — ${player.paused ? "Paused" : "Playing"}`; pause.textContent = player.paused ? "Resume" : "Pause"; };
  const paint = () => {
    if (!loaded || !player.scene) return;
    const offset = player.offset; context.fillStyle = "#000"; context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(loaded.image, -offset.x, -offset.y);
    if (player.circular) {
      if (loaded.plan.outputWidth > canvas.width) context.drawImage(loaded.image, loaded.plan.outputWidth - offset.x, 0);
      else if (loaded.plan.outputHeight > canvas.height) context.drawImage(loaded.image, 0, loaded.plan.outputHeight - offset.y);
    }
    for (const overlay of loaded.overlays) context.drawImage(overlay.image, overlay.region.x, overlay.region.y, overlay.region.width, overlay.region.height);
  };
  const load = async () => {
    const operation = ++generation, index = player.index; loaded = null; player.scene = null; last = null; pause.disabled = true; retry.hidden = true;
    navigation.forEach(button => { button.disabled = true; });
    update("Preparing artwork..."); context.clearRect(0, 0, canvas.width, canvas.height);
    let timeout;
    try {
      if (!cache.has(index)) {
        if (cache.size >= 2) cache.delete(cache.keys().next().value);
        cache.set(index, (async () => {
        const item = player.items[index], scene = await prepareItem(item, index);
        const image = await loadImage(scene.url);
        if (image.naturalWidth !== scene.plan.outputWidth || image.naturalHeight !== scene.plan.outputHeight) throw new Error("The artwork preview does not match its selected layout.");
        const images = [...effectiveStaticOverlays(item), ...(scene.interactions?.touchAreas ?? []).filter(area => area.overlayImage).map(area => ({ region: area.region, image: area.overlayImage }))];
        const overlays = await Promise.all(images.map(async overlay => ({ region: overlay.region, image: await loadImage(overlayPreview(overlay.image)) })));
        return { ...scene, image, overlays };
        })());
      }
      const scene = await Promise.race([cache.get(index), new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error("Artwork preview took too long to load. Retry after checking your connection.")), 20000); })]);
      if (closed || operation !== generation || !isCurrent()) return;
      loaded = scene; player.setScene(scene); canvas.width = scene.plan.viewport.width; canvas.height = scene.plan.viewport.height;
      pause.disabled = false; update(); paint();
    } catch (error) {
      if (operation === generation) cache.delete(index);
      if (!closed && operation === generation) { update(error.customerSafe ? error.message : "This artwork preview could not load. Retry after checking the artwork and connection."); retry.hidden = false; }
    } finally { clearTimeout(timeout); if (!closed && operation === generation) navigation.forEach(button => { button.disabled = false; }); }
  };
  const animate = timestamp => {
    if (closed) return;
    if (!dialog.isConnected || !isCurrent()) return close();
    const wasPaused = player.paused;
    if (!document.hidden && last !== null && player.tick(timestamp - last)) void load();
    if (loaded && wasPaused !== player.paused) update();
    last = timestamp; paint();
    if (loaded) pause.textContent = player.paused ? "Resume" : "Pause";
    frame = requestAnimationFrame(animate);
  };
  dialog.querySelector("[data-preview-close]").addEventListener("click", close);
  dialog.addEventListener("cancel", event => { event.preventDefault(); close(); });
  dialog.addEventListener("close", close);
  dialog.querySelector("[data-preview-next]").addEventListener("click", () => { if (player.next()) void load(); });
  dialog.querySelector("[data-preview-previous]").addEventListener("click", () => { if (player.next(-1)) void load(); });
  dialog.querySelector("[data-preview-back]").addEventListener("click", () => { if (player.back()) void load(); });
  pause.addEventListener("click", () => { player.togglePause(); update(); });
  retry.addEventListener("click", () => void load());
  canvas.addEventListener("keydown", event => { if (event.code === "Space") { event.preventDefault(); player.togglePause(); update(); } });
  canvas.addEventListener("click", event => {
    if (!loaded) return;
    const rect = canvas.getBoundingClientRect(), result = player.tap((event.clientX - rect.left) * canvas.width / rect.width, (event.clientY - rect.top) * canvas.height / rect.height);
    if (result === "navigate") void load();
    else update(result === "unavailable" ? "This hotspot's destination is unavailable in the draft." : result === "unassigned" ? "Choose an action for this hotspot in the editor." : undefined);
  });
  dialog.querySelector("[data-preview-resume]").addEventListener("change", event => { player.resumeDelayMs = Number(event.target.value); player.idle = 0; });
  dialog.showModal(); canvas.focus();
  if (!context) { update("This browser cannot draw the preview. Reopen it in a current browser."); pause.disabled = true; }
  else { void load(); frame = requestAnimationFrame(animate); }
  return close;
}
